import {
  Avatar,
  Box,
  Flex,
  HStack,
  IconButton,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  VStack,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Mention, MentionsInput } from 'react-mentions';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from 'urql';
import { Point2 } from '../../domain/geometry/geometric-types';
import {
  CommentDeepFragment,
  GetCommentDeepDocument,
  ListAllCommentsDocument,
  ListUsersDocument,
} from '../../gql/graphql';
import { useUserTenant } from '../../services/auth-info';
import SparkelIcon from '../common/icon/SparkelIcon';
import {
  projectInDOM,
  useCameraChangedEvent,
} from '../orderContractor/shapes/util';
import Comment from './Comment';

import CommentWithReplies from './CommentWithReplies';
import { useComments } from './useComments';

type CommentOverlayProps = {
  viewer: Autodesk.Viewing.Viewer3D;
};

// eslint-disable-next-line complexity
export function CommentOverlay({ viewer }: CommentOverlayProps) {
  const [searchParams] = useSearchParams();
  const commentId = searchParams.get('commentId');
  const { reply } = useComments();

  const {
    isOpen: showNewPopover,
    onOpen: openNewPopover,
    onClose: closeNewPopover,
  } = useDisclosure();

  const [{ data: commentsDataQuery }] = useQuery({
    query: GetCommentDeepDocument,
    pause: !commentId,
    variables: { id: commentId ? commentId : '' },
  });

  //Should only fetch relevant replies in the future
  const [{ data: listAllCommentsDataQuery }] = useQuery({
    query: ListAllCommentsDocument,
    pause: !commentId,
  });

  const commentsWithRepliesMap = new Map<string, CommentDeepFragment[]>();

  if (listAllCommentsDataQuery && listAllCommentsDataQuery.comments)
    listAllCommentsDataQuery?.comments.forEach((comment) => {
      const parentId = comment.parentId || '';
      if (!commentsWithRepliesMap.has(parentId)) {
        commentsWithRepliesMap.set(parentId, []);
      }
      commentsWithRepliesMap.get(parentId)?.push(comment);
    });

  const [cameraPosition, setCameraPosition] = useState({});
  const commentData = commentsDataQuery?.comment;
  const replies = commentData ? commentsWithRepliesMap.get(commentData.id) : [];
  const point = commentsDataQuery?.comment?.anchorPoint3D
    ? commentsDataQuery?.comment?.anchorPoint3D
    : null;

  const [{ data: listUsersData }] = useQuery({
    query: ListUsersDocument,
  });

  const position2D = useMemo<Point2>(
    () => projectInDOM(point ? [point.x, point.y, point.z] : [0, 0, 0], viewer),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [point, viewer, cameraPosition]
  );

  const [commentText, setCommentText] = useState('');

  const AuthUser = useUserTenant().username;

  const _handleClick = () => {
    if (commentText === '') return;
    reply(commentId, AuthUser, commentText, commentData?.username);
    setCommentText('');
  };

  useCameraChangedEvent(
    viewer,
    useCallback(() => {
      setCameraPosition({});
    }, [])
  );

  useEffect(() => {
    if (commentId) {
      openNewPopover();
    } else {
      closeNewPopover();
    }
  }, [closeNewPopover, commentId, openNewPopover]);

  const replyInputRef = useRef<HTMLInputElement | null>(null);

  const replyInputBackgroundColor = useColorModeValue('gray.100', 'gray.700');

  return (
    <Popover
      closeOnBlur={false}
      isOpen={showNewPopover}
      onClose={closeNewPopover}
      isLazy
      initialFocusRef={replyInputRef}
      autoFocus
    >
      {commentData ? (
        <PopoverContent
          width={'fit-content'}
          maxWidth={'sm'}
          style={{
            left: position2D[0] - 5,
            top: position2D[1] - 5,
          }}
          borderRadius={'2xl'}
          borderTopLeftRadius={'sm'}
          shadow={'xl'}
        >
          <PopoverBody padding={2} paddingY={0}>
            <PopoverHeader height={6}>
              <PopoverCloseButton borderRadius={'full'} />
            </PopoverHeader>

            <Flex alignItems={'start'} justifyContent="space-between">
              <VStack gap={0}>
                {replies && replies.length > 0 ? (
                  <CommentWithReplies
                    key={commentData.id}
                    CommentComponent={
                      <Comment
                        key={commentData.id}
                        id={commentData.id}
                        content={commentData.content}
                        createdAt={commentData.createdAtTz}
                        user={{ username: commentData.username }}
                        projectId={commentData.projectId}
                        takeoffId={
                          commentData.orderId ? commentData.orderId : undefined
                        }
                        readBy={commentData.readBy ? commentData.readBy : []}
                        closeParentPopover={closeNewPopover}
                        elementIdentifiers={
                          commentData.elementIdentifiers
                            ? commentData.elementIdentifiers
                            : []
                        }
                        savedViews={
                          commentData.savedViews
                            ? commentData.savedViews
                            : undefined
                        }
                        commentId={commentData.id}
                        readOnlyMode
                      />
                    }
                    replies={replies}
                    id={commentData.id}
                  />
                ) : (
                  <Comment
                    key={commentData.id}
                    content={commentData.content}
                    id={commentData.id}
                    createdAt={commentData.createdAtTz}
                    user={{ username: commentData.username }}
                    projectId={''}
                    takeoffId=""
                    readBy={commentData.readBy ? commentData.readBy : []}
                    elementIdentifiers={[]}
                    savedViews={
                      commentData.savedViews
                        ? commentData.savedViews
                        : undefined
                    }
                    commentId={commentData.id}
                    readOnlyMode
                  />
                )}
                <HStack gap={1} height={'full'} width={'100%'}>
                  <Avatar
                    name={AuthUser}
                    size="xs"
                    marginTop={1}
                    marginLeft={2}
                    alignSelf={'start'}
                  />
                  <Box
                    width={'100%'}
                    backgroundColor={replyInputBackgroundColor}
                    minHeight={'2em'}
                    borderRadius={'md'}
                    paddingTop={1}
                    paddingLeft={1}
                  >
                    <MentionsInput
                      inputRef={replyInputRef} // Needed to focus on input when popover opens
                      style={{
                        input: {
                          paddingLeft: '4px',
                          outline: 'none',
                          fontSize: '0.9em',
                          paddingTop: '2px',
                          maxWidth: '100%',
                        },
                      }}
                      value={commentText}
                      autoFocus
                      onChange={(e) => {
                        setCommentText(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          _handleClick();
                        }
                      }}
                      placeholder={'Reply'}
                    >
                      <Mention
                        trigger="@"
                        data={
                          listUsersData?.listUsers
                            ? listUsersData?.listUsers.map((user) => ({
                                id: user.id,
                                display: user.email,
                              }))
                            : []
                        }
                        renderSuggestion={(suggestion) => (
                          <div>{suggestion.display}</div>
                        )}
                      />
                    </MentionsInput>
                  </Box>
                  {commentText !== '' ? (
                    <IconButton
                      icon={
                        <SparkelIcon
                          icon={faArrowUp}
                          color={['white', 'gray.800']}
                        />
                      }
                      size={'xs'}
                      onClick={() => _handleClick()}
                      aria-label={'send comment'}
                      marginTop={1}
                      alignSelf={'start'}
                    />
                  ) : null}
                </HStack>
              </VStack>
            </Flex>
          </PopoverBody>
        </PopoverContent>
      ) : null}
    </Popover>
  );
}
